<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Course' : 'Add Course'"
      :breadcrumb="breadcrumb"
      :container-class="null"
      class="mb-32pt"
    />

    <page-separator title="Course" />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Image" label-for="image" label-class="form-label" class="row-align-items-center">
        <b-media class="align-items-center" vertical-align="center">
          <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
            <span v-if="isImgUploading">...</span>
            <b-img :src="course.image" class="img-fluid" width="40" alt="Image" v-else-if="course.image" />
            <i class="fas fa-university fa-lg" v-else></i>
          </fmv-avatar>

          <image-uploader
            @image="e => setImage(e, 'image')"
            :isUploading="isImgUploading"
            :isRequired="!course.image"
            placeholder="Select Image"
          />
        </b-media>
      </b-form-group>

      <b-form-group label="Title" label-for="title" label-class="form-label">
        <b-form-input id="title" placeholder="Enter Course's Title" v-model="course.title" required />
      </b-form-group>

      <b-form-group label="Short Introduction" label-for="short_introduction" label-class="form-label">
        <b-form-textarea
          id="short_introduction"
          placeholder="Enter Course's Short Introduction"
          v-model="course.shortIntro"
          required
          :rows="3"
        />
      </b-form-group>

      <b-form-group label="Description" label-for="description" label-class="form-label">
        <editor
          id="description"
          name="description"
          :init="{
            height: 400,
            menubar: 'edit view insert format tools table help',
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount',
            ],
            toolbar:
              'undo redo | formatselect | bold italic backcolor | \
             alignleft aligncenter alignright alignjustify | \
             bullist numlist outdent indent | removeformat | help',
          }"
          v-model="course.description"
          placeholder="Enter Course Description."
        />
        <b-form-invalid-feedback :state="!$v.course.description.required && $v.course.description.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group label="Program Category" label-for="prog_category" label-class="form-label">
        <v-select
          required
          id="prog_category"
          class="form-control v-select-custom"
          label="title"
          v-model="course.progCategory"
          :reduce="item => item.id"
          placeholder="Select Program Category"
          :options="allPrograms"
          :disabled="!canUpdateCategory"
        >
        </v-select>
        <b-form-invalid-feedback
          :state="!$v.course.progCategory.required && $v.course.progCategory.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>
      <b-form-group>
        <b-form-checkbox id="checkbox-4" v-model="course.isPublished" name="checkbox-4" switch
          >Course Published?
        </b-form-checkbox>
      </b-form-group>
      <div class="row mb-4">
        <span class="form-label col-md-6">Intro Video</span>
        <div class="col-md-6" style="display: flex; justify-content: end;">
          <b-btn
            v-if="!course.previewVideoUrl"
            variant="primary"
            :class="{ 'mb-2': course.previewVideoUrl }"
            style="width: 150px"
            @click.prevent="$refs.uploader.click()"
            class="btn-normal"
            :disabled="isUploadingVideoUrl"
          >
            <i v-if="isUploadingVideoUrl" class="fas fa-circle-notch fa-spin"></i>
            <span v-else>Upload Intro</span>
          </b-btn>
          <b-btn
            v-else
            variant="danger"
            :class="{ 'mb-2': course.previewVideoUrl }"
            style="width: 150px"
            @click.prevent="removeVideo"
            class="btn-normal"
            :disabled="isUploadingVideoUrl"
          >
            <span>Remove Video</span>
          </b-btn>
          <input type="file" ref="uploader" class="d-none" @change="handleFileChange" accept="video/*" />
        </div>
        <div v-if="course.previewVideoUrl" class="d-flex align-items-center col">
          <video
            style="width: 100%; margin: auto;"
            :style="{ height: isMobileScreen ? '230px' : '450px' }"
            id="video-preview"
            controls
            :src="course.previewVideoUrl"
          />
        </div>
      </div>
      <b-btn
        variant="primary"
        :disabled="isFormLoading || isUploadingVideoUrl"
        style="width: 150px"
        type="submit"
        class="btn-normal"
      >
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
//import { get, map } from 'lodash';
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import Editor from '@tinymce/tinymce-vue';
import Page from '../../../components/Page.vue';
// import UserAvatar from '../../../components/User/UserAvatar.vue';
import 'vue-swatches/dist/vue-swatches.css';
import ImageUploader from '@/components/ImageUploader.vue';
import { FmvAvatar } from 'fmv-avatar';
import { required } from 'vuelidate/lib/validators';
export default {
  components: {
    PageHeader,
    PageSeparator,
    Editor,
    ImageUploader,
    FmvAvatar,
  },
  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit Course' : 'Add Course',
      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,
      course: {
        title: '',
        image: '',
        description: '',
        shortIntro: '',
        progCategory: '',
        previewVideoUrl: '',
        isPublished: true,
      },
      canUpdateCategory: true,
      uploadFile: { image: null, video: null },
      uploadPercent: { image: 0, video: 0 },
      uploadCancelTokenSource: { image: null, video: null },
      isLoading: false,
      isImgUploading: false,
      areCareersLoading: false,
      allPrograms: [],
      isUploadingVideoUrl: false,
      windowWidth: window.innerWidth,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Courses', to: { name: 'list-courses' } },
        {
          text: this.$route.params.id ? 'Edit Course' : 'Add Course',
          active: true,
        },
      ];
    },
    isFormLoading() {
      return this.isLoading || this.isLogoUploading || this.isProfileBgImgUploading;
    },
    isMobileScreen() {
      return this.windowWidth <= 420;
    },
  },
  validations() {
    return {
      course: {
        description: { required },
        progCategory: { required },
      },
    };
  },
  methods: {
    ...mapActions('courses', [
      'getCourse',
      'createCourse',
      'updateCourse',
      'getCourseImageUploadPresignedUrl',
      'canUpdateCourseCategory',
    ]),
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    setImage(file, fileType) {
      this.uploadFile[fileType] = file;
      this.uploadImage(fileType);
    },
    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms();
        this.allPrograms = res.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadVideo(file);
      }
    },
    async uploadVideo(file) {
      this.isUploadingVideoUrl = true;

      if (file.size > 25000000) {
        this.makeToast({ variant: 'danger', msg: 'File size should not exceed 25MB' });
        this.isUploadingVideoUrl = false;
        return;
      }
      if (!['video/mp4', 'video/webm'].includes(file.type)) {
        this.makeToast({ variant: 'danger', msg: 'File type should be mp4 or webm' });

        this.isUploadingVideoUrl = false;
        return;
      }

      try {
        const urlResp = await this.getCourseImageUploadPresignedUrl({
          file_name: file.name,
          content_type: file.type,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.data.upload_url,
          file: file,
        });
        this.course.previewVideoUrl = urlResp.data.upload_url.split('?')[0];
      } catch (error) {
        this.isUploadingVideoUrl = false;
        return;
      }
      this.isUploadingVideoUrl = false;
    },
    async uploadImage(uploadType) {
      this.uploadCancelTokenSource[uploadType] = axios.CancelToken.source();
      this.isImgUploading = uploadType === 'image';

      try {
        const urlResp = await this.getCourseImageUploadPresignedUrl({
          file_name: this.uploadFile[uploadType].name,
          content_type: this.uploadFile[uploadType].type,
          upload_type: 'images',
        });
        await this.uploadToPresignedUrl({
          url: urlResp.data.upload_url,
          file: this.uploadFile[uploadType],
          config: {
            onUploadProgress: function(progressEvent) {
              this.uploadPercent[uploadType] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource[uploadType].token,
          },
        });
        if (uploadType === 'image') {
          this.course.image = urlResp.data.upload_url.split('?')[0];
        }
      } catch (error) {
        this.uploadFile[uploadType] = null;
      }

      this.uploadCancelTokenSource[uploadType] = null;
      this.uploadPercent[uploadType] = 0;

      if (uploadType === 'image') this.isImgUploading = false;
    },

    async onSubmit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.isLoading = true;
        try {
          const data = {
            title: this.course.title,
            short_intro: this.course.shortIntro,
            description: this.course.description,
            image_url: this.course.image,
            program_category: this.course.progCategory,
            preview_video_url: this.course.previewVideoUrl,
            is_published: this.course.isPublished,
            school: this.getLoggedInUser.linked_entity.id,
          };
          if (this.$route.params.id) {
            await this.updateCourse({
              id: this.$route.params.id,
              data,
            });
            this.makeToast({ variant: 'success', msg: this.$t('Course Updated!') });
          } else {
            await this.createCourse({
              ...data,
            });
            this.makeToast({ variant: 'success', msg: 'Course Added!' });
          }
          setTimeout(() => this.$router.push({ name: 'list-courses' }), 250);
        } catch (err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }

        this.isLoading = false;
      }
    },
    removeVideo() {
      this.course.previewVideoUrl = '';
    },
  },

  async mounted() {
    this.isLoading = true;
    this.fetchPrograms();
    try {
      if (this.$route.params.id) {
        const course_id = this.$route.params.id;
        const response = (await this.canUpdateCourseCategory({ course_id: course_id })).data;
        this.canUpdateCategory = response.can_update;
        const resp = (await this.getCourse(course_id)).data;
        this.course.title = resp.title;
        this.course.description = resp.description;
        this.course.shortIntro = resp.short_intro;
        this.course.previewVideoUrl = resp.preview_video_url;
        this.course.image = resp.image_url;
        this.course.progCategory = resp.program_category.id;
        this.course.isPublished = resp.is_published;
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      this.$router.push({ name: 'list-courses' });
    }

    this.isLoading = false;
  },
};
</script>
